/**
 * Country inference for Xtream catalogs.
 *
 * Xtream almost never carries a real country field, so the code has to be read
 * out of the category name (and, failing that, the channel name). The naive
 * approach — scanning the whole string for /\b[A-Z]{2,3}\b/ — is badly wrong:
 * it matches THE, NEW, TOP, HD, PPV, and "us"/"it"/"in"/"no" inside ordinary
 * English words, and it happily labels "NEW MOVIES" as Norway.
 *
 * What actually works is structural. IPTV category names are separator-
 * delimited: "US: Entertainment", "UK | Sports", "[CA] News", "DE- News",
 * "USA SPORTS", "FR ● CINEMA". So:
 *
 *   1. split the name on the separators panels actually use,
 *   2. take each segment in first-then-last order,
 *   3. accept a segment only when the WHOLE segment is a known country token,
 *      or when exactly one of its words is and every other word is known noise
 *      ("USA SPORTS" -> US, "HD SPORTS" -> no match).
 *
 * A segment must match a curated vocabulary. Bare ISO alpha-2 is accepted as a
 * fallback but a small deny-list removes the codes that mean something else far
 * more often than they mean a country (TV is Tuvalu; SD is Sudan).
 *
 * The parser is a guess and is treated as one: the UI shows a histogram, every
 * unmatched row lands in an explicit "Other" bucket, and a per-category manual
 * override always wins.
 */

export type CountryConfidence = 'segment' | 'word' | 'none';

export interface CountryGuess {
  /** ISO 3166-1 alpha-2, or null for the "Other" bucket. */
  code: string | null;
  confidence: CountryConfidence;
  /** The literal text that produced the match, for debugging/UI. */
  matched: string | null;
}

const NO_MATCH: CountryGuess = { code: null, confidence: 'none', matched: null };

/**
 * `code|alias|alias|...` — first field is the ISO code, the rest are tokens
 * that map to it. Only countries that realistically appear in IPTV lists are
 * enumerated; anything else falls through to the bare alpha-2 check.
 */
const VOCABULARY = [
  // The first alias longer than three characters becomes the display label,
  // so the spelled-out name leads and the spaced form stays a lookup alias.
  'US|USA|UNITED STATES|U S A|UNITED STATES OF AMERICA|AMERICA|AMERICAN|US EAST|US WEST|USA EAST|USA WEST',
  'GB|UK|GBR|UNITED KINGDOM|ENG|ENGLAND|GREAT BRITAIN|BRITAIN|BRITISH|SCOTLAND|WALES|IRELAND NORTHERN',
  'CA|CAN|CANADA|CANADIAN|CANADA FR|CANADA EN',
  'AU|AUS|AUSTRALIA|AUSTRALIAN',
  'NZ|NZL|NEW ZEALAND',
  'IE|IRL|IRELAND|EIRE',
  'DE|DEU|GER|GERMANY|GERMAN|DEUTSCHLAND',
  'AT|AUT|AUSTRIA|OSTERREICH',
  'CH|CHE|SUI|SWITZERLAND|SCHWEIZ|SUISSE|SVIZZERA',
  'FR|FRA|FRANCE|FRENCH|FRANCAIS',
  'BE|BEL|BELGIUM|BELGIQUE|BELGIE',
  'NL|NLD|HOL|HOLLAND|NETHERLANDS|NEDERLAND|DUTCH',
  'LU|LUX|LUXEMBOURG',
  'ES|ESP|SPAIN|SPANISH|ESPANA|ESPANOL',
  'PT|PRT|POR|PORTUGAL|PORTUGUESE|PORTUGUES',
  'IT|ITA|ITALY|ITALIA|ITALIAN|ITALIANO',
  'GR|GRC|GRE|GREECE|GREEK|HELLAS|ELLADA|ΕΛΛΑΔΑ|ΕΛΛΑΣ',
  'PL|POL|POLAND|POLSKA|POLISH',
  'CZ|CZE|CZECH|CZECH REPUBLIC|CESKO|CESKA',
  'SK|SVK|SLOVAKIA|SLOVENSKO',
  'HU|HUN|HUNGARY|MAGYAR|MAGYARORSZAG',
  'RO|ROU|ROM|ROMANIA|ROMANIAN|ROMANESC',
  'BG|BGR|BUL|BULGARIA|BULGARIAN|БЪЛГАРИЯ|БГ',
  'RS|SRB|SER|SERBIA|SRBIJA|СРБИЈА|СРБИЈА',
  'HR|HRV|CRO|CROATIA|HRVATSKA',
  'BA|BIH|BOSNIA|BOSNA|BOSNIA AND HERZEGOVINA',
  'SI|SVN|SLO|SLOVENIA|SLOVENIJA',
  'MK|MKD|MACEDONIA|NORTH MACEDONIA|MAKEDONIJA',
  'ME|MNE|MONTENEGRO|CRNA GORA',
  'AL|ALB|ALBANIA|SHQIP|SHQIPERI|ALBANIAN',
  'XK|KOSOVO|KOSOVA',
  'SE|SWE|SWEDEN|SVERIGE|SWEDISH',
  'NO|NOR|NORWAY|NORGE|NORSK|NORWEGIAN',
  'DK|DNK|DEN|DENMARK|DANMARK|DANISH',
  'FI|FIN|FINLAND|SUOMI|FINNISH',
  'IS|ISL|ICELAND|ISLAND',
  'EE|EST|ESTONIA|EESTI',
  'LV|LVA|LATVIA|LATVIJA',
  'LT|LTU|LITHUANIA|LIETUVA',
  'RU|RUS|RUSSIA|RUSSIAN|ROSSIYA|РОССИЯ|РУС|РОС',
  'UA|UKR|UKRAINE|UKRAINIAN|UKRAINA|УКРАЇНА|УКРАИНА|УКР',
  'BY|BLR|BELARUS|БЕЛАРУСЬ',
  'MD|MDA|MOLDOVA',
  'GE|GEO|GEORGIA',
  'AM|ARM|ARMENIA',
  'AZ|AZE|AZERBAIJAN',
  'KZ|KAZ|KAZAKHSTAN',
  'UZ|UZB|UZBEKISTAN',
  'TR|TUR|TURKEY|TURKIYE|TURKISH|TURK',
  'CY|CYP|CYPRUS',
  'MT|MLT|MALTA',
  'IL|ISR|ISRAEL',
  'SA|SAU|KSA|SAUDI|SAUDI ARABIA',
  'AE|ARE|UAE|EMIRATES|UNITED ARAB EMIRATES|DUBAI|ABU DHABI',
  'QA|QAT|QATAR',
  'KW|KWT|KUWAIT',
  'BH|BHR|BAHRAIN',
  'OM|OMN|OMAN',
  'JO|JOR|JORDAN',
  'LB|LBN|LEBANON',
  'SY|SYR|SYRIA',
  'IQ|IRQ|IRAQ',
  'IR|IRN|IRAN|PERSIAN|FARSI',
  'YE|YEM|YEMEN',
  'EG|EGY|EGYPT',
  'MA|MAR|MOROCCO|MAROC',
  'DZ|DZA|ALG|ALGERIA|ALGERIE',
  'TN|TUN|TUNISIA|TUNISIE',
  'LY|LBY|LIBYA',
  'SS|SOUTH SUDAN',
  'ZA|ZAF|RSA|SOUTH AFRICA',
  'NG|NGA|NIGERIA',
  'GH|GHA|GHANA',
  'KE|KEN|KENYA',
  'TZ|TZA|TANZANIA',
  'UG|UGA|UGANDA',
  'ET|ETH|ETHIOPIA',
  'SN|SEN|SENEGAL',
  'CI|CIV|IVORY COAST|COTE DIVOIRE',
  'CM|CMR|CAMEROON',
  'AO|AGO|ANGOLA',
  'MZ|MOZ|MOZAMBIQUE',
  'ZW|ZWE|ZIMBABWE',
  'IN|IND|INDIA|INDIAN|HINDI|DESI',
  'PK|PAK|PAKISTAN',
  'BD|BGD|BANGLADESH|BANGLA',
  'LK|LKA|SRI LANKA',
  'NP|NPL|NEPAL',
  'AF|AFG|AFGHANISTAN',
  'CN|CHN|CHINA|CHINESE',
  'HK|HKG|HONG KONG',
  'TW|TWN|TAIWAN',
  'JP|JPN|JAPAN|JAPANESE',
  'KR|KOR|KOREA|SOUTH KOREA|KOREAN',
  'TH|THA|THAILAND|THAI',
  'VN|VNM|VIETNAM|VIET NAM',
  'PH|PHL|PHILIPPINES|PINOY|FILIPINO',
  'ID|IDN|INDONESIA',
  'MY|MYS|MALAYSIA',
  'SG|SGP|SINGAPORE',
  'MM|MMR|MYANMAR|BURMA',
  'KH|KHM|CAMBODIA',
  'MX|MEX|MEXICO|MEXICAN',
  'BR|BRA|BRAZIL|BRASIL|BRAZILIAN',
  'AR|ARG|ARGENTINA',
  'CL|CHL|CHILE',
  'CO|COL|COLOMBIA',
  'PE|PER|PERU',
  'VE|VEN|VENEZUELA',
  'EC|ECU|ECUADOR',
  'UY|URY|URUGUAY',
  'PY|PRY|PARAGUAY',
  'BO|BOL|BOLIVIA',
  'CR|CRI|COSTA RICA',
  'PA|PAN|PANAMA',
  'GT|GTM|GUATEMALA',
  'HN|HND|HONDURAS',
  'SV|SLV|EL SALVADOR',
  'NI|NIC|NICARAGUA',
  'DO|DOM|DOMINICAN REPUBLIC|REPUBLICA DOMINICANA',
  'CU|CUB|CUBA',
  'PR|PRI|PUERTO RICO',
  'JM|JAM|JAMAICA',
  'TT|TTO|TRINIDAD|TRINIDAD AND TOBAGO',
  'HT|HTI|HAITI',
  'BB|BRB|BARBADOS',
] as const;

/** Tokens that may sit beside a country token inside one segment. */
const NOISE_WORDS = new Set<string>([
  // quality / format
  'HD', 'FHD', 'UHD', 'SD', 'HQ', 'LQ', '4K', '8K', '2K', '1080P', '720P', '480P',
  'HEVC', 'H264', 'H265', 'X264', 'X265', 'HDR', 'DOLBY', 'ATMOS', '3D', '50FPS', '60FPS',
  'RAW', 'BACKUP', 'ALT', 'MULTI', 'DUAL', 'MULTISUB', 'SUB', 'SUBBED', 'DUB', 'DUBBED',
  // marketing
  'VIP', 'PREMIUM', 'PLUS', 'EXTRA', 'PRO', 'GOLD', 'SILVER', 'BASIC', 'FULL', 'MAIN',
  'NEW', 'TOP', 'HOT', 'BEST', 'ALL', 'MISC', 'OTHER', 'OTHERS', 'GENERAL', 'GLOBAL',
  'WORLD', 'INTL', 'INTERNATIONAL', 'EXCLUSIVE', 'ULTRA', 'MEGA', 'SUPER',
  // content buckets
  'TV', 'CHANNEL', 'CHANNELS', 'CH', 'LIVE', 'VOD', 'EPG', 'IPTV', 'STREAM', 'STREAMS',
  'SPORT', 'SPORTS', 'NEWS', 'KIDS', 'CHILDREN', 'CARTOON', 'CARTOONS', 'MOVIE', 'MOVIES',
  'CINEMA', 'SERIES', 'SHOWS', 'DOC', 'DOCS', 'DOCUMENTARY', 'DOCUMENTARIES', 'MUSIC',
  'RADIO', 'ADULT', 'XXX', 'PPV', 'EVENT', 'EVENTS', 'ENTERTAINMENT', 'LIFESTYLE',
  'RELIGION', 'RELIGIOUS', 'KIDSTV', 'KIDZ', 'KID', 'KID S',
  // leagues / orgs that ride along
  'NBA', 'NFL', 'MLB', 'NHL', 'UFC', 'EPL', 'MLS', 'WWE', 'F1', 'MOTOGP', 'UCL', 'NCAA',
  // structural
  'AND', 'THE', 'OF', 'FOR', '24', '7', '247', '24 7',
  // Genre words in the languages IPTV catalogs are actually written in. Without
  // these, "NL Nieuws" or "DE Nachrichten" fail the all-other-words-are-noise
  // test and fall into Other even though the country tag is unambiguous.
  'NIEUWS', 'SPORTEN', 'KINDEREN', 'FILMS', 'ZENDERS',
  'NACHRICHTEN', 'UNTERHALTUNG', 'KINDER', 'SPORTEN DE', 'FILME', 'SENDER', 'DOKU',
  'DEPORTES', 'NOTICIAS', 'PELICULAS', 'INFANTIL', 'ENTRETENIMIENTO', 'CANALES', 'MUSICA',
  'FILMES', 'ESPORTES', 'NOTICIAS BR', 'DESPORTO', 'CANAIS', 'CRIANCAS',
  'ACTUALITES', 'ENFANTS', 'DIVERTISSEMENT', 'CHAINES', 'MUSIQUE', 'SPORTIFS',
  'NOTIZIE', 'BAMBINI', 'INTRATTENIMENTO', 'CANALI', 'SPORTIVI',
  'HABER', 'SPOR', 'COCUK', 'MUZIK', 'EGLENCE', 'KANALLAR',
  'WIADOMOSCI', 'DZIECI', 'ROZRYWKA', 'KANALY', 'FILMY', 'MUZYKA',
  'NYHETER', 'BARN', 'UNDERHALLNING', 'KANALER',
  'UUTISET', 'LAPSET', 'KANAVAT',
  'STIRI', 'COPII', 'DIVERTISMENT', 'CANALE',
  'NOVINY', 'DETI', 'ZABAVA', 'KANALU',
  'HIREK', 'GYEREK', 'SZORAKOZAS', 'CSATORNAK',
  'NOVOSTI', 'DETSKIE', 'RAZVLECHENIYA',
  'AKHBAR', 'AFLAM', 'ATFAL', 'RIYADA',
  // Cyrillic and Greek equivalents, since names in those scripts survive
  // normalization now.
  'НОВОСТИ', 'СПОРТ', 'ДЕТСКИЕ', 'ФИЛЬМЫ', 'КАНАЛЫ', 'МУЗЫКА', 'РАЗВЛЕЧЕНИЯ',
  'ΕΙΔΗΣΕΙΣ', 'ΑΘΛΗΤΙΚΑ', 'ΠΑΙΔΙΚΑ', 'ΤΑΙΝΙΕΣ', 'ΜΟΥΣΙΚΗ',
]);

/**
 * Valid ISO alpha-2 codes that mean something else far more often in a
 * channel-list context than they mean the country.
 */
const AMBIGUOUS_ALPHA2 = new Set(['TV', 'SD', 'HD', 'AD', 'AI', 'PM', 'ST', 'VI', 'IO', 'TD', 'UM']);

/** Alpha-3 codes that are also everyday English words. */
const AMBIGUOUS_ALPHA3 = new Set(['AND', 'ARE', 'CAN', 'PER', 'MAR', 'BRA', 'ATA', 'ALA']);

const ALL_ALPHA2 = new Set(
  ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS ' +
   'BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE ' +
   'EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM ' +
   'HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC ' +
   'LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA ' +
   'NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW ' +
   'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO ' +
   'TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW').split(' '),
);

/** alias token -> ISO code. Built once at module load. */
const ALIAS_TO_CODE: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const row of VOCABULARY) {
    const parts = row.split('|');
    const code = parts[0];
    for (const alias of parts) {
      const key = alias.trim();
      // The vocabulary is authoritative, so a later row never steals an
      // earlier row's alias (first definition wins).
      if (key && !map.has(key)) map.set(key, code);
    }
  }
  return map;
})();

/** Separators panels use between a country tag and the rest of the name. */
const SEPARATORS = /[|:/\\[\](){}<>~•●▎▍│┃!#*+,;_]+|\s[-–—]\s|^[-–—]|[-–—]$|(?<=[A-Za-z])[-–—](?=\s)/g;

const DIACRITICS = /[̀-ͯ]/g;

/** Uppercase, strip diacritics/punctuation, collapse whitespace. */
export function normalizeToken(value: string): string {
  return value
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toUpperCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function lookupToken(token: string): string | null {
  if (!token) return null;
  const direct = ALIAS_TO_CODE.get(token);
  if (direct) return direct;

  const compact = token.replace(/\s+/g, '');
  const compactHit = ALIAS_TO_CODE.get(compact);
  if (compactHit) return compactHit;

  if (compact.length === 2 && ALL_ALPHA2.has(compact) && !AMBIGUOUS_ALPHA2.has(compact)) {
    return compact;
  }
  return null;
}

/**
 * Split a display name into the segments a country tag could occupy, in the
 * order we should trust them: leading segment first, then trailing, then the
 * middle ones.
 */
function orderedSegments(name: string): string[] {
  const raw = name.split(SEPARATORS).map((s) => normalizeToken(s)).filter(Boolean);
  if (raw.length <= 1) return raw;
  const [first, ...rest] = raw;
  const last = rest.pop() as string;
  return [first, last, ...rest];
}

function matchSegment(segment: string): CountryGuess | null {
  const whole = lookupToken(segment);
  if (whole) return { code: whole, confidence: 'segment', matched: segment };

  const words = segment.split(' ').filter(Boolean);
  if (words.length < 2 || words.length > 5) return null;

  let hit: { code: string; word: string } | null = null;
  for (const word of words) {
    // "ALL YOU CAN WATCH" must not become Canada. These aliases are only
    // trusted when they are the entire segment, which is handled above.
    const code = AMBIGUOUS_ALPHA3.has(word) ? null : lookupToken(word);
    if (code) {
      // Two different country tokens in one segment ("US UK 24/7") is not a
      // country label, it is a bundle. Refuse rather than pick one.
      if (hit && hit.code !== code) return null;
      if (!hit) hit = { code, word };
      continue;
    }
    if (!NOISE_WORDS.has(word) && !/^\d+$/.test(word)) return null;
  }
  return hit ? { code: hit.code, confidence: 'word', matched: hit.word } : null;
}

/** Infer a country from a single display name. */
export function inferCountryFromName(name: string | null | undefined): CountryGuess {
  if (!name) return NO_MATCH;
  for (const segment of orderedSegments(name)) {
    const hit = matchSegment(segment);
    if (hit) return hit;
  }
  return NO_MATCH;
}

/**
 * Infer a country for a stream. The category name is far more reliable than the
 * channel name ("BBC News HD" has no country in it; "UK | News" does), so it is
 * consulted first and the channel name is only a fallback.
 */
export function inferCountry(categoryName: string | null | undefined, streamName?: string | null): CountryGuess {
  const fromCategory = inferCountryFromName(categoryName);
  if (fromCategory.code) return fromCategory;
  return inferCountryFromName(streamName);
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

const DISPLAY_NAMES = new Map<string, string>(
  VOCABULARY.map((row) => {
    const parts = row.split('|');
    // Prefer the first multi-word/long alias as the human label, else the code.
    const label = parts.slice(1).find((p) => p.length > 3) ?? parts[0];
    return [parts[0], titleCase(label)] as [string, string];
  }),
);

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .split(' ')
    .map((word) => (word.length <= 2 ? word.toUpperCase() : word[0].toUpperCase() + word.slice(1)))
    .join(' ');
}

/** Human label for a country bucket. `null` is the catch-all bucket. */
export function countryLabel(code: string | null): string {
  if (!code) return 'Other';
  return DISPLAY_NAMES.get(code) ?? code;
}

/** Regional-indicator flag for an alpha-2 code. Empty string for the null bucket. */
export function flagEmoji(code: string | null): string {
  if (!code || code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(base + (code.charCodeAt(0) - 65), base + (code.charCodeAt(1) - 65));
}

/**
 * The codes worth offering in a manual-override picker, most-seen-first.
 *
 * The parser is a heuristic, so a human has to be able to correct it — but
 * offering all 249 ISO codes turns a correction into a scavenger hunt. This is
 * the set that actually appears in IPTV catalogs.
 */
export const COMMON_COUNTRY_CODES: readonly string[] = [
  'US', 'GB', 'CA', 'AU', 'IE', 'NZ',
  'DE', 'FR', 'IT', 'ES', 'PT', 'NL', 'BE', 'AT', 'CH', 'LU',
  'SE', 'NO', 'DK', 'FI', 'IS',
  'PL', 'CZ', 'SK', 'HU', 'RO', 'BG', 'GR', 'TR', 'CY', 'MT',
  'RS', 'HR', 'BA', 'SI', 'MK', 'ME', 'AL', 'XK',
  'RU', 'UA', 'BY', 'MD', 'GE', 'AM', 'AZ', 'KZ', 'EE', 'LV', 'LT',
  'IL', 'SA', 'AE', 'QA', 'KW', 'BH', 'OM', 'JO', 'LB', 'SY', 'IQ', 'IR', 'YE',
  'EG', 'MA', 'DZ', 'TN', 'LY', 'ZA', 'NG', 'GH', 'KE', 'ET', 'SN', 'CI',
  'IN', 'PK', 'BD', 'LK', 'NP', 'AF',
  'CN', 'HK', 'TW', 'JP', 'KR', 'TH', 'VN', 'PH', 'ID', 'MY', 'SG',
  'MX', 'BR', 'AR', 'CL', 'CO', 'PE', 'VE', 'EC', 'UY', 'PY', 'BO',
  'CR', 'PA', 'GT', 'HN', 'SV', 'NI', 'DO', 'CU', 'PR', 'JM', 'TT',
];

/** True when `code` is a code this module could ever produce. */
export function isSupportedCountryCode(code: string): boolean {
  return ALL_ALPHA2.has(code) && !AMBIGUOUS_ALPHA2.has(code);
}
